import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Monitor, Smartphone, Laptop, Tablet, Globe, Trash2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { confirm as brandedConfirm } from "@/components/ui/confirm-dialog";

/**
 * Parses a raw User-Agent string into a friendly "Chrome on macOS" label
 * and returns the matching device icon. Not exhaustive — covers the 90%
 * case (Chrome/Firefox/Safari/Edge × Windows/macOS/Linux/iOS/Android).
 */
function parseUserAgent(ua) {
  if (!ua) return { label: "Unknown device", Icon: Globe };
  const s = ua.toLowerCase();
  let browser = "Browser";
  if (s.includes("edg/")) browser = "Edge";
  else if (s.includes("firefox")) browser = "Firefox";
  else if (s.includes("chrome") && !s.includes("edg")) browser = "Chrome";
  else if (s.includes("safari") && !s.includes("chrome")) browser = "Safari";
  else if (s.includes("curl")) browser = "API (curl)";
  else if (s.includes("postman")) browser = "API (Postman)";
  let os = "Unknown OS"; let Icon = Monitor;
  if (s.includes("iphone")) { os = "iPhone"; Icon = Smartphone; }
  else if (s.includes("ipad")) { os = "iPad"; Icon = Tablet; }
  else if (s.includes("android")) { os = "Android"; Icon = Smartphone; }
  else if (s.includes("mac os x") || s.includes("macintosh")) { os = "macOS"; Icon = Laptop; }
  else if (s.includes("windows")) { os = "Windows"; Icon = Laptop; }
  else if (s.includes("linux")) { os = "Linux"; Icon = Laptop; }
  return { label: `${browser} on ${os}`, Icon };
}

function relativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.toLocaleDateString();
}

/**
 * Active-sessions card — HIPAA audit feature. Users see every device
 * currently logged in as them, with a "Revoke" button for each + a panic
 * "Log out all other sessions" button.
 */
export default function ActiveSessionsCard() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/profile/sessions");
      setSessions(data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async (sessionId) => {
    if (!(await brandedConfirm({ title: 'Are you sure?', description: "Sign this session out? Any device currently using it will be logged out immediately.", confirmText: 'Confirm', cancelText: 'Cancel', tone: 'info' }))) return;
    setRevoking(sessionId);
    try {
      await API.delete(`/profile/sessions/${sessionId}`);
      toast.success("Session revoked");
      load();
    } catch { toast.error("Failed to revoke"); } finally { setRevoking(""); }
  };

  const revokeAllOthers = async () => {
    if (!(await brandedConfirm({ title: 'Are you sure?', description: "Sign out every device except this one? Useful if you think your account may be compromised.", confirmText: 'Confirm', cancelText: 'Cancel', tone: 'warn' }))) return;
    try {
      const { data } = await API.post("/profile/sessions/revoke-others");
      toast.success(`Signed out ${data.revoked} other session${data.revoked === 1 ? "" : "s"}`);
      load();
    } catch { toast.error("Failed to revoke"); }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6" data-testid="active-sessions-card">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h3 className="font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Active Sessions</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Every device currently signed in to your account. Revoke anything you don&apos;t recognise.</p>
        </div>
        {sessions.filter(s => !s.is_current).length > 0 && (
          <Button variant="outline" size="sm" onClick={revokeAllOthers} className="rounded-lg text-xs text-red-600 border-red-200 dark:border-red-900/40 hover:bg-red-50 dark:hover:bg-red-900/20 flex-shrink-0" data-testid="revoke-all-others-btn">
            <LogOut className="w-3.5 h-3.5 mr-1" /> Sign out others
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 dark:text-slate-500">Loading sessions...</div>
      ) : sessions.length === 0 ? (
        <div className="text-sm text-slate-400 dark:text-slate-500">No active sessions.</div>
      ) : (
        <div className="space-y-2">
          {sessions.map(s => {
            const { label, Icon } = parseUserAgent(s.user_agent);
            return (
              <div key={s.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-100 dark:border-slate-800/60 bg-slate-50/50 dark:bg-slate-800/30" data-testid={`session-${s.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${s.is_current ? "bg-emerald-100 text-emerald-600" : "bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-400"}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-slate-900 dark:text-white truncate">{label}</span>
                      {s.is_current && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid="session-current-badge">This device</span>}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                      <span>IP: {s.ip || "—"}</span>
                      <span>&middot;</span>
                      <span>Last seen {relativeTime(s.last_seen)}</span>
                    </div>
                  </div>
                </div>
                {!s.is_current && (
                  <Button size="sm" variant="ghost" onClick={() => revoke(s.id)} disabled={revoking === s.id} className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex-shrink-0" data-testid={`revoke-session-${s.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
